import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Title } from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { auth } from "./firebaseClient";
import {
  ACTIVE_RIDE_STATUSES,
  DASHBOARD_RIDE_STATUSES,
  formatDateTime,
  loadSupplierDrivers,
  loadSupplierProfile,
  loadSupplierRides,
  statusLabel,
  toDateAny,
} from "./supplierApi";

function MetricCard({ title, value, helper, icon, onClick }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
      <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">{title}</Typography>
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{helper}</Typography>
            </Box>
            <Box sx={{ color: "primary.main", display: "flex", pt: 0.25 }}>{icon}</Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function SupplierDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [profileData, rideData, driverData] = await Promise.all([
        loadSupplierProfile(),
        loadSupplierRides(DASHBOARD_RIDE_STATUSES),
        loadSupplierDrivers(),
      ]);
      setProfile(profileData);
      setRides(rideData);
      setDrivers(driverData);
    } catch (e) {
      console.error("Supplier dashboard failed:", e);
      setError("Some dashboard information could not be loaded. Please use Refresh or contact Comfort Line if the issue continues.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stats = useMemo(() => ({
    newOffers: rides.filter((r) => r.status === "shared").length,
    accepted: rides.filter((r) => r.status === "accepted").length,
    active: rides.filter((r) => ACTIVE_RIDE_STATUSES.includes(r.status)).length,
    drivers: drivers.length,
  }), [rides, drivers]);

  const nextTrips = useMemo(() => {
    const now = Date.now();
    return rides
      .filter((r) => !["completed", "cancelled", "guestNoShow"].includes(r.status))
      .filter((r) => (toDateAny(r.pickupTimestamp)?.getTime() || 0) >= now - 3 * 60 * 60 * 1000)
      .sort((a, b) => (toDateAny(a.pickupTimestamp)?.getTime() || 0) - (toDateAny(b.pickupTimestamp)?.getTime() || 0))
      .slice(0, 6);
  }, [rides]);

  const company = profile?.companyName || profile?.name || "Supplier Partner";

  return (
    <Box>
      <Title title="Supplier Dashboard" />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.2, md: 3 },
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <Box>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <DashboardCustomizeIcon sx={{ color: "primary.main" }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Supplier Dashboard</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 0.8 }}>
              Review booking offers, assign chauffeurs and manage your fleet from one place.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.65 }}>
              {company} · {auth.currentUser?.email || "supplier"}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <Button variant="contained" onClick={() => navigate("/new-bookings")}>New Bookings</Button>
            <Button variant="outlined" onClick={() => navigate("/my-drivers")}>My Drivers</Button>
            <Button variant="text" onClick={refresh} disabled={loading}>Refresh</Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && rides.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2.2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="New Booking Offers" value={stats.newOffers} helper="Waiting for your response" icon={<MarkEmailUnreadOutlinedIcon fontSize="large" />} onClick={() => navigate("/new-bookings")} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Accepted" value={stats.accepted} helper="Waiting for chauffeur assignment" icon={<TaskAltIcon fontSize="large" />} onClick={() => navigate("/accepted-bookings")} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="Active Rides" value={stats.active} helper="Assigned or in progress" icon={<DirectionsCarFilledOutlinedIcon fontSize="large" />} onClick={() => navigate("/active-rides")} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title="My Drivers" value={stats.drivers} helper="Your supplier fleet" icon={<PeopleAltOutlinedIcon fontSize="large" />} onClick={() => navigate("/my-drivers")} />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.2, md: 3 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              minHeight: 225,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Next Supplier Trips</Typography>
              <Button size="small" onClick={() => navigate("/active-rides")}>View all</Button>
            </Stack>

            {nextTrips.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography color="text.secondary">No upcoming supplier trips found.</Typography>
              </Box>
            ) : (
              <Stack spacing={1.2}>
                {nextTrips.map((ride) => (
                  <Box
                    key={ride.id}
                    sx={{
                      p: 1.8,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                      gap: 1.2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{ride.passengerName || "Guest"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ride.pickupLocation || "Pickup"} → {ride.dropLocation || "Drop-off"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(ride.pickupTimestamp)} · {ride.tripId || ride.id}
                      </Typography>
                    </Box>
                    <Chip label={statusLabel(ride.status)} size="small" color={ride.status === "shared" ? "primary" : "default"} />
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
